import { gql, useQuery } from '@apollo/client';
import * as React from 'react';

import { type IGateUser } from './adminSelectUsersHelpers';

import type {
  AdminListUsersForGateQuery,
  AdminListUsersForGateQueryVariables,
} from './__generated__/useAdminGateUsers.gql';

// Distinct operation name from `AdminSelectUser`'s `AdminListUsers` so
// near-operation-file codegen doesn't collide on the shared
// `adminListUsers` field. Shared by every gate picker and the edit-diff
// email resolver; Apollo's normalized cache means they fetch once.
export const ADMIN_LIST_USERS_FOR_GATE = gql`
  query AdminListUsersForGate {
    adminListUsers {
      id
      email
      firstName
      lastName
    }
  }
`;

export interface IAdminGateUsers {
  users: IGateUser[];
  usersById: Map<string, IGateUser>;
  loading: boolean;
}

/**
 * Loads every user once for the gate pickers and the edit-diff email
 * resolver. Backed by a single `adminListUsers` query, so Apollo's
 * normalized cache means multiple callers share one fetch.
 *
 * @returns An object with `users` (all loaded users), `usersById` (the
 * same users keyed by id for O(1) lookup), and `loading` (true until the
 * query first resolves).
 */
export function useAdminGateUsers(): IAdminGateUsers {
  const { data, loading } = useQuery<AdminListUsersForGateQuery, AdminListUsersForGateQueryVariables>(
    ADMIN_LIST_USERS_FOR_GATE
  );
  const users = React.useMemo<IGateUser[]>(() => data?.adminListUsers ?? [], [data?.adminListUsers]);
  const usersById = React.useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  return { users, usersById, loading };
}
